import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LocaleService } from '@shared/services/locale.service';

export type Region = 'HK' | 'KLN' | 'NT';
export type RegionFilter = 'ALL' | Region;

export interface Store {
  id: string;
  nameZh: string;
  nameEn: string;
  addressZh: string;
  addressEn: string;
  hours: string;
  phone: string;
  region: Region;
  mapQuery: string;
}

export const STORES: Store[] = [
  {
    id: 'mong-kok-mtr',
    nameZh: '旺角港鐵站分店',
    nameEn: 'Mong Kok MTR',
    addressZh: '旺角港鐵站11號鋪 (近E出口)',
    addressEn: 'MOK11 Mong Kok MTR Station (Near Exit E).',
    hours: '11:00 - 22:00',
    phone: '(852) 2392 1218',
    region: 'KLN',
    mapQuery: 'CHEZ+CHOUX+Mong+Kok+MTR'
  },
  {
    id: 'kwun-tong-mtr',
    nameZh: '觀塘港鐵站分店',
    nameEn: 'Kwun Tong MTR',
    addressZh: '觀塘港鐵站21號鋪 (近A出口)',
    addressEn: 'KWT21 Kwun Tong MTR Station (Near Exit A).',
    hours: '11:00 - 22:00',
    phone: '(852) 2698 1798',
    region: 'KLN',
    mapQuery: 'CHEZ+CHOUX+Kwun+Tong+MTR'
  },
  {
    id: 'mostown',
    nameZh: '馬鞍山 MOSTown 分店',
    nameEn: 'MOSTown',
    addressZh: '馬鞍山 MOSTown 新港城中心 2224-25 號鋪',
    addressEn: 'Shop 2224-25, MOSTown, Ma On Shan',
    hours: '10:30 - 22:00',
    phone: '(852) 2657 2898',
    region: 'NT',
    mapQuery: 'CHEZ+CHOUX+MOSTown+Ma+On+Shan'
  },
  {
    id: 'sha-tin-mtr',
    nameZh: '沙田港鐵站分店',
    nameEn: 'Sha Tin MTR',
    addressZh: '沙田港鐵站37號鋪',
    addressEn: 'SHT37 Sha Tin MTR Station.',
    hours: '11:00 - 22:00',
    phone: '(852) 2699 9906',
    region: 'NT',
    mapQuery: 'CHEZ+CHOUX+Sha+Tin+MTR'
  },
  {
    id: 'tuen-mun-mtr',
    nameZh: '屯門港鐵站分店',
    nameEn: 'Tuen Mun MTR',
    addressZh: '屯門港鐵站54號鋪',
    addressEn: 'TUM54 Tuen Mun MTR Station.',
    hours: '11:00 - 22:00',
    phone: '(852) 2660 0878',
    region: 'NT',
    mapQuery: 'MTR+Tuen+Mun+Station'
  },
  {
    id: 'fanling-mtr',
    nameZh: '粉嶺港鐵站分店',
    nameEn: 'Fanling MTR',
    addressZh: '粉嶺港鐵站1號舖',
    addressEn: 'FAN 1 Fanling MTR Station',
    hours: '11:00 - 22:00',
    phone: '(852) 2332 1078',
    region: 'NT',
    mapQuery: 'CHEZ+CHOUX+Fanling+MTR'
  }
];

interface RegionChip { value: RegionFilter; labelZh: string; labelEn: string; }

@Component({
  selector: 'app-store-locator',
  templateUrl: './store-locator.component.html',
  styleUrls: ['./store-locator.component.scss']
})
export class StoreLocatorComponent implements OnInit {
  readonly stores = STORES;

  readonly regionChips: RegionChip[] = [
    { value: 'ALL', labelZh: $localize`:@@storeLocator.region.all:全部`, labelEn: 'All' },
    { value: 'KLN', labelZh: $localize`:@@storeLocator.region.kln:九龍`, labelEn: 'KLN' },
    { value: 'NT',  labelZh: $localize`:@@storeLocator.region.nt:新界`,  labelEn: 'NT' },
    { value: 'HK',  labelZh: $localize`:@@storeLocator.region.hk:港島`,  labelEn: 'HK' }
  ];

  query = '';
  activeRegion: RegionFilter = 'ALL';
  activeStoreId = STORES[0].id;

  constructor(private sanitizer: DomSanitizer, private locale: LocaleService) {}

  ngOnInit(): void {}

  storeName(s: Store): string {
    return this.locale.isEn ? (s.nameEn || s.nameZh) : s.nameZh;
  }

  storeSubName(s: Store): string {
    return this.locale.isEn ? s.nameZh : (s.nameEn || '');
  }

  storeAddress(s: Store): string {
    return this.locale.isEn ? (s.addressEn || s.addressZh) : s.addressZh;
  }

  storeSubAddress(s: Store): string {
    return this.locale.isEn ? s.addressZh : (s.addressEn || '');
  }

  get filteredStores(): Store[] {
    const q = this.query.trim().toLowerCase();
    return this.stores.filter(s => {
      if (this.activeRegion !== 'ALL' && s.region !== this.activeRegion) return false;
      if (!q) return true;
      return (s.nameZh + s.nameEn + s.addressZh + s.addressEn).toLowerCase().includes(q);
    });
  }

  get activeStore(): Store {
    return this.stores.find(s => s.id === this.activeStoreId) || this.stores[0];
  }

  get mapSrc(): SafeResourceUrl {
    const url = `https://www.google.com/maps?q=${this.activeStore.mapQuery}&z=15&output=embed`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  selectStore(id: string): void { this.activeStoreId = id; }
  selectRegion(r: RegionFilter): void { this.activeRegion = r; }
  trackById(_i: number, s: Store): string { return s.id; }
}
